/**
 * Shared authenticated fetch helpers for API calls.
 */

import { AUTH_TOKEN_TIMEOUT_MS } from '@/config/constants'
import type { FilterCondition } from '@/utils/filterUtils'
import { withTimeout } from './withTimeout'

export interface Pagination {
  limit: number
  has_more: boolean
  next_cursor: string | null
  total: number | null
}

export interface ListQueryParams {
  limit?: number
  sort_by?: string
  sort_order?: 'asc' | 'desc'
  filters?: FilterCondition[]
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

export function buildListSearchParams(
  params: ListQueryParams,
  cursor?: string
): URLSearchParams {
  const search = new URLSearchParams()
  if (params.limit) search.set('limit', params.limit.toString())
  if (cursor) search.set('cursor', cursor)
  if (params.sort_by) search.set('sort_by', params.sort_by)
  if (params.sort_order) search.set('sort_order', params.sort_order)

  for (const filter of params.filters ?? []) {
    const value = filter.value.trim()
    if (value) {
      search.append(`filter[${filter.field}][${filter.operator}]`, value)
    }
  }

  return search
}

export function parsePagination(
  pagination: Partial<Pagination> | null | undefined,
  defaultLimit: number
): Pagination {
  return {
    limit: pagination?.limit ?? defaultLimit,
    has_more: pagination?.has_more ?? false,
    next_cursor: pagination?.next_cursor ?? null,
    total: pagination?.total ?? null,
  }
}

/**
 * Fetches the auth token with a bound, and rejects when there isn't one.
 *
 * Clerk's `getToken()` awaits an internal "loaded" promise that has no timeout
 * and no reject path, so a bare `await getToken()` can hang indefinitely — in a
 * React Query `queryFn` that means the query never settles, which renders as a
 * permanent skeleton with no error state and no retry surface.
 *
 * Most `getToken()` call sites in the app are still bare; they should move to
 * this helper.
 */
export async function requireAuthToken(
  getToken: () => Promise<string | null>
): Promise<string> {
  const token = await withTimeout(
    getToken(),
    AUTH_TOKEN_TIMEOUT_MS,
    'Timed out waiting for authentication'
  )
  if (!token) throw new Error('Not authenticated')
  return token
}

/**
 * Thrown when a fetch returns a non-OK status. Subclass of Error so existing
 * `catch (e: Error)` paths still work; the `status` property lets callers
 * distinguish expected statuses (e.g. 404 "not yet finalized") from generic
 * failures without parsing the message.
 */
export class ApiError extends Error {
  readonly status: number
  /** Machine-readable reason from the API (e.g. `duplicate_name`), when it sends one. */
  readonly code?: string

  constructor(
    message: string,
    status: number,
    code?: string,
    readonly details?: Record<string, unknown>
  ) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
  }
}

/**
 * Reads an error response body and shapes it into an `ApiError`. Both helpers
 * below use it, so a caller can branch on `.status` no matter which one it
 * called — without parsing the message text.
 */
export async function apiErrorFrom(
  response: Response,
  errorMessage: string
): Promise<ApiError> {
  const text = await response.text().catch(() => '')
  let message = text
  let code: string | undefined
  let details: Record<string, unknown> | undefined
  try {
    const body = JSON.parse(text) as Record<string, unknown>
    details =
      typeof body.detail === 'object' &&
      body.detail !== null &&
      !Array.isArray(body.detail)
        ? (body.detail as Record<string, unknown>)
        : body
    const structuredMessage =
      details.message ?? body.detail ?? body.message ?? body.error
    if (typeof structuredMessage === 'string') {
      message = structuredMessage
    }
    if (typeof details.code === 'string') {
      code = details.code
    }
  } catch {
    // Keep non-JSON response bodies as-is.
  }
  return new ApiError(
    message || `${errorMessage}: ${response.status}`,
    response.status,
    code,
    details
  )
}

export async function fetchJsonWithAuth<T>({
  token,
  url,
  signal,
  errorMessage,
}: {
  token: string
  url: string
  signal?: AbortSignal
  errorMessage: string
}): Promise<T> {
  const response = await fetch(url, {
    headers: { Authorization: token },
    signal,
  })

  if (!response.ok) throw await apiErrorFrom(response, errorMessage)

  return (await response.json()) as T
}

export async function mutateJsonWithAuth<T>({
  token,
  url,
  method,
  body,
  errorMessage,
}: {
  token: string
  url: string
  method: 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  body?: unknown
  errorMessage: string
}): Promise<T> {
  const response = await fetch(url, {
    method,
    headers: {
      Authorization: token,
      'Content-Type': 'application/json',
    },
    body: typeof body === 'undefined' ? undefined : JSON.stringify(body),
  })

  if (!response.ok) throw await apiErrorFrom(response, errorMessage)

  const contentLength = response.headers.get('content-length')
  if (response.status === 204 || contentLength === '0') {
    return undefined as T
  }

  return (await response.json()) as T
}
